import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/base/SeoHead';
import { useTheme } from '@/context/ThemeContext';
import { LegalPage } from '@/context/LegalContext';
import { useBadgeVisibility } from '@/context/BadgeVisibilityContext';

interface Props {
  page: LegalPage;
  seoTitle: string;
  seoDescription: string;
  canonical: string;
  breadcrumb: string;
}

export default function LegalPageLayout({ page, seoTitle, seoDescription, canonical, breadcrumb }: Props) {
  const { theme } = useTheme();
  const { settings } = useBadgeVisibility();
  const light = theme === 'light';

  const bg = light ? 'bg-gray-50' : 'bg-[#0D1B2A]';
  const textPrimary = light ? 'text-gray-900' : 'text-white';
  const textSecondary = light ? 'text-gray-500' : 'text-white/50';
  const textMuted = light ? 'text-gray-400' : 'text-white/30';
  const cardBg = light ? 'bg-white border border-gray-100' : 'bg-[#1C2B3A] border border-white/5';
  const divider = light ? 'border-gray-100' : 'border-white/5';

  const formattedDate = new Date(page.lastUpdated).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className={`min-h-screen ${bg} transition-colors duration-300`}>
      <SeoHead
        title={seoTitle}
        description={seoDescription}
        keywords="Stingray Marine, battery warranty, marine battery policy"
        canonical={canonical}
      />
      <Navbar />

      {/* Hero */}
      <header className={`pt-28 pb-14 w-full px-6 md:px-10 ${bg}`}>
        <div className="max-w-3xl mx-auto text-center">
          {settings.legalBadges && (
            <span
              className="inline-block text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-5"
              style={{
                background: 'color-mix(in srgb, var(--accent) 12%, transparent)',
                border: '1px solid color-mix(in srgb, var(--accent) 25%, transparent)',
                color: 'var(--accent)',
              }}
            >
              Legal
            </span>
          )}
          <h1 className={`text-4xl md:text-5xl font-black mb-4 ${textPrimary}`}>{page.title}</h1>
          <p className={`text-base max-w-xl mx-auto mb-4 ${textSecondary}`}>{page.subtitle}</p>
          <p className={`text-xs ${textMuted}`}>Last updated: {formattedDate}</p>
        </div>
      </header>

      <main className="w-full px-6 md:px-10 pb-24">
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <nav className={`flex items-center gap-2 text-xs mb-8 ${textMuted}`}>
            <Link to="/" className="hover:text-[var(--accent)] transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className={light ? 'text-gray-600' : 'text-white/60'}>{breadcrumb}</span>
          </nav>

          {/* Table of Contents */}
          {page.sections.length > 2 && (
            <div className={`rounded-2xl p-6 mb-8 ${cardBg}`}>
              <p className={`text-xs font-bold uppercase tracking-widest mb-4 ${textMuted}`}>Contents</p>
              <ol className="space-y-2">
                {page.sections.map((section, i) => (
                  <li key={i}>
                    <a
                      href={`#section-${i}`}
                      className={`flex items-center gap-3 text-sm transition-colors hover:text-[var(--accent)] cursor-pointer ${textSecondary}`}
                    >
                      <span
                        className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black text-white shrink-0"
                        style={{ background: 'var(--accent)' }}
                      >
                        {i + 1}
                      </span>
                      {section.heading}
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Sections */}
          <div className="space-y-6">
            {page.sections.map((section, i) => (
              <div key={i} id={`section-${i}`} className={`rounded-2xl p-5 sm:p-7 ${cardBg} scroll-mt-28`}>
                <div className="flex items-start gap-4 mb-4">
                  <span
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-black text-white shrink-0 mt-0.5"
                    style={{ background: 'var(--accent)' }}
                  >
                    {i + 1}
                  </span>
                  <h2 className={`text-lg font-bold ${textPrimary}`}>{section.heading}</h2>
                </div>
                <div className={`border-t ${divider} pt-4`}>
                  <p className={`text-sm leading-relaxed whitespace-pre-line ${textSecondary}`}>{section.body}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Footer note */}
          <div className={`mt-10 rounded-2xl p-6 text-center ${cardBg}`}>
            <i className="ri-mail-line text-2xl mb-3 block" style={{ color: 'var(--accent)' }}></i>
            <p className={`text-sm font-semibold mb-1 ${textPrimary}`}>Questions about this policy?</p>
            <p className={`text-xs mb-4 ${textSecondary}`}>Our team is happy to help clarify anything.</p>
            <Link
              to="/contact"
              className="inline-flex w-full sm:w-auto justify-center items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white cursor-pointer whitespace-nowrap"
              style={{ background: 'var(--accent)' }}
            >
              <i className="ri-customer-service-line"></i>
              Contact Us
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
